import React, { useState } from 'react';
import { InfoCircleOutlined, ClockCircleOutlined, CheckCircleOutlined, ExclamationCircleFilled } from '@ant-design/icons';
import { Typography, Space, Tag, Button, Image, Divider } from 'antd';
import { BlindTypeMap, MergedOrder, OrderStatusMap } from '../../../types/process/order';
import { ORDER_STATUS_LABEL_MAP, type OrderTags, SendTypeMap, ReturnTypeMap, LogisticsNameMap } from '../../../types/process/order-data';
import { useShopMap } from '../../../services/store/organization.store';
import { formatRiskQualitative } from './effect';
import { AccessoryModal } from './modals/AccessoryModal';
import { BuyoutInfoModal } from './modals/BuyoutInfoModal';
import { OperationLogModal } from './modals/OperationLogModal';
import { LogisticsTrackModal } from './modals/LogisticsTrackModal';
import OrderPreRatingSummary from './OrderPreRatingSummary';

const { Text, Paragraph } = Typography;

interface OrderBlockShowProps {
  order: MergedOrder;
  // 支持传入额外的操作渲染，如果不传则使用默认
  renderActions?: (order: MergedOrder) => React.ReactNode;
  renderInfo?: (order: MergedOrder) => React.ReactNode;
  headerExtra?: React.ReactNode;
}

const OrderBlockShow: React.FC<OrderBlockShowProps> = ({ order, renderActions, renderInfo, headerExtra }) => {
  const { order: orderEntity, orderData, orderRecord } = order;
  const { shopMap } = useShopMap();
  const [isAccessoryModalVisible, setIsAccessoryModalVisible] = useState(false);
  const [isBuyoutModalVisible, setIsBuyoutModalVisible] = useState(false);
  const [isLogModalVisible, setIsLogModalVisible] = useState(false);
  const [isTrackModalVisible, setIsTrackModalVisible] = useState(false);

  const isApplyingCancel = orderRecord?.cmptStat?.apply_order === 1;
  const cancelData = orderRecord?.cancelData;

  // Unified style constants (based on previous non-compact values)
  const imageSize = 80;
  const mainPadding = 16;
  const topPadding = '12px 16px'; // Increased padding slightly
  const dividerMargin = '8px 0';
  const mainMinHeight = 180;
  const productFlex = 2.5;
  const statusFlex = 1.5;
  const fontSize = 12;

  /**
   * 安全展示字段：空值统一展示为 '-'
   */
  const safeGet = (val: unknown, suffix = '') => (val === undefined || val === null || val === '' ? '-' : `${val}${suffix}`);

  /**
   * 金额格式化：空值展示为 '-'
   */
  const formatMoney = (val: unknown) => (val === undefined || val === null || val === '' ? '-' : `¥${Number(val).toFixed(2)}`);

  /**
   * 地址展示：优先使用 userAddress；否则拼接省市区（过滤空值）
   */
  const formatAddress = () => {
    if (orderData?.userAddress) return orderData.userAddress;
    const parts = [orderData?.province, orderData?.city, orderData?.district].filter((p) => p);
    return parts.length > 0 ? parts.join('') : '-';
  };

  const shopName = shopMap.get(orderEntity.storeId)?.name || orderEntity.storeId;

  const tags: Partial<OrderTags> = orderData?.tags ?? {
    zftOrder: orderData?.zftOrder,
    payType: orderData?.payType,
    cancelControl: orderData?.cancelControl,
  };

  const getCancelReasonText = (reason: string | undefined | null) => {
    if (!reason) return '-';
    try {
      if (reason.startsWith('[') || reason.startsWith('{')) {
        const parsed = JSON.parse(reason);
        if (Array.isArray(parsed) && parsed.length > 0) {
          return parsed[0].title || reason;
        } else if (typeof parsed === 'object' && parsed.title) {
          return parsed.title || reason;
        }
      }
    } catch (e) {
      // ignore error, return original string
    }
    return reason;
  };

  return (
    <div style={{
      border: '1px solid #f0f0f0',
      marginBottom: 16,
      background: '#fff',
      fontSize
    }}>
      {/* Top Bar: Order Info */}
      <div style={{
        background: '#f5f7fa',
        padding: topPadding,
        borderBottom: '1px solid #f0f0f0'
      }}>
        {/* Row 1 */}
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 16 }}>
          <Space size={24}>
            <Text type="secondary">{new Date(orderEntity.orderCreatedAt).toLocaleString()}</Text>
            <Text type="secondary">订单号: <Text strong style={{ color: '#000' }}>{orderEntity.orderNo}</Text></Text>
            <Text type="secondary">店铺: {shopName}</Text>
          </Space>
          <OrderPreRatingSummary orderNo={orderEntity.orderNo} preRating={order.preRating} headerExtra={headerExtra} />
        </div>

        {/* Row 2 */}
        <div style={{ marginTop: 8, display: 'flex', gap: 32, flexWrap: 'wrap' }}>
          <Text type="secondary">订单续租: {orderData?.supportRenewal ? '开启' : '关闭'}</Text>
          <Text type="secondary">订单结算比例: {safeGet(orderData?.settlementRatio)}</Text>
          <Text type="secondary">
            寄出单号: {orderData?.logisticsInfo?.logisticNumber ? (
              <>
                <Text strong style={{ color: '#1890ff', cursor: 'pointer' }} onClick={() => setIsTrackModalVisible(true)}>
                  {orderData.logisticsInfo.logisticNumber}
                </Text>
                {orderData.logisticsInfo.logisticName && (
                  <Text style={{ marginLeft: 4 }}>
                    【{LogisticsNameMap[orderData.logisticsInfo.logisticName] || orderData.logisticsInfo.logisticName}】
                  </Text>
                )}
              </>
            ) : '-'}
          </Text>
          <Text type="secondary">续租每期费率: {safeGet(orderData?.buyoutRate)}</Text>
          <Text type="secondary">寄出方式: {orderData?.logisticsInfo?.sendType ? (SendTypeMap[orderData.logisticsInfo.sendType] || orderData.logisticsInfo.sendType) : '-'}</Text>
          <Text type="secondary">归还方式: {orderData?.logisticsInfo?.returnType ? (ReturnTypeMap[orderData.logisticsInfo.returnType] || orderData.logisticsInfo.returnType) : '-'}</Text>
        </div>

        {/* Tags Row - Below Order Info (A) */}
        <div style={{ marginTop: 8 }}>
          <Space>
            {tags?.zftOrder && <Tag color="red">直付通订单</Tag>}
            {tags?.payType &&
              (tags.payType === '支付宝免密代扣' ? <Tag color="red">{tags.payType}</Tag> : <Tag color="blue">{tags.payType}</Tag>)}
            {tags?.renewalOrder && <Tag color="blue">续租</Tag>}
            {orderData?.supportRenewal && <Tag color="green">支持续租</Tag>}
            {orderData?.appType && <Tag color="blue">{orderData.appType}</Tag>}
            {orderEntity.blindType && <Tag color="orange">{BlindTypeMap[orderEntity.blindType]}</Tag>}
          </Space>
        </div>
      </div>

      {/* Main Content */}
      <div style={{ display: 'flex', minHeight: mainMinHeight }}>
        {/* Col 1: Product (Flex: 2.5) */}
        <div style={{ flex: productFlex, padding: mainPadding, borderRight: '1px solid #f0f0f0' }}>
          <div style={{ display: 'flex', gap: 12 }}>
            <div style={{ display: 'flex', alignItems: 'flex-start', gap: 8 }}>
              <Image
                width={imageSize}
                height={imageSize}
                src={orderData?.spuCoverUrl || "error"}
                style={{ borderRadius: 4, objectFit: 'cover' }}
              />
              {((orderData?.accessoryData?.length || 0) > 0) && (
                <Button type="link" size="small" onClick={() => setIsAccessoryModalVisible(true)} style={{ padding: 0 }}>
                  配件清单({orderData?.accessoryData?.length})
                </Button>
              )}
            </div>
          </div>
          <div style={{ marginTop: 12, display: 'flex', flexDirection: 'column', gap: 4 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
              <Text strong style={{ fontSize: 14 }}>
                {orderData?.productTitle || '未知商品'}
              </Text>
              {orderData?.productTitle && <Paragraph copyable={{ text: orderData.productTitle }} style={{ margin: 0, color: '#1890ff' }} />}
            </div>
            <Text type="secondary">
              套餐: {orderData?.skuName || '-'}
            </Text>
            <Text type="secondary">
              租期: {orderData?.tenancyData?.start_time ? new Date(orderData.tenancyData.start_time).toLocaleDateString() : '-'} ~
              {orderData?.tenancyData?.end_time ? new Date(orderData.tenancyData.end_time).toLocaleDateString() : '-'}
              {orderData?.tenancyData?.total_days ? ` (共${orderData.tenancyData.total_days}天)` : ''}
            </Text>
          </div>
        </div>

        {/* Col 2: Money (Flex: 2) */}
        <div style={{ flex: 2, padding: mainPadding, borderRight: '1px solid #f0f0f0', display: 'flex', flexDirection: 'column', justifyContent: 'space-between' }}>
          <div>
            <div style={{ display: 'flex', justifyContent: 'space-between' }}>
              <Text type="secondary">押金:</Text>
              <Text>{formatMoney(orderData?.depositValue)}</Text>
            </div>
            <div style={{ display: 'flex', justifyContent: 'space-between' }}>
              <Text type="secondary">信用评估额度:</Text>
              <Text>{orderData?.creditEvaluatieQuota ? `¥${Number(orderData.creditEvaluatieQuota).toFixed(2)}` : '-'}</Text>
            </div>
            <div style={{ display: 'flex', justifyContent: 'space-between' }}>
              <Text type="secondary">商品押金:</Text>
              <Text>{formatMoney(orderData?.productValue)}</Text>
            </div>
            <div style={{ display: 'flex', justifyContent: 'space-between' }}>
              <Text type="secondary">已冻结押金:</Text>
              <Text style={{ color: '#ff4d4f' }}>{formatMoney(orderData?.alreadyFrozen)}</Text>
            </div>
          </div>
          <Divider style={{ margin: dividerMargin }} />
          <div>
            <div style={{ display: 'flex', justifyContent: 'space-between' }}>
              <Text type="secondary">租金:</Text>
              <Text>{formatMoney(orderData?.receivableRental || orderData?.rentalNeedPay)}</Text>
            </div>
            <div style={{ display: 'flex', justifyContent: 'space-between' }}>
              <Text type="secondary">首期:</Text>
              <Text>{formatMoney(orderData?.firstRent || orderData?.installmentPrice)}</Text>
            </div>
          </div>
          <Divider style={{ margin: dividerMargin }} />
          <div style={{ display: 'flex', justifyContent: 'space-between' }}>
            <Text type="secondary">数量:</Text>
            <Text>{orderData?.quantity}</Text>
          </div>
        </div>

        {/* Col 3: Paid & Rent (Flex: 2) - (C, F) */}
        <div style={{ flex: 2, padding: mainPadding, borderRight: '1px solid #f0f0f0', display: 'flex', flexDirection: 'column', justifyContent: 'space-between' }}>
          <div style={{ marginBottom: 8 }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 4 }}>
              <Text type="secondary">实付款: </Text>
              <Text strong style={{ fontSize: 14 }}>{formatMoney(orderData?.hadPayRental || orderData?.receivedRental || 0)}</Text>
            </div>
            {/* Payment Time: Formatted and Red (C) */}
            <div style={{ display: 'flex', justifyContent: 'space-between' }}>
              <Text type="secondary">付款时间: </Text>
              <Text style={{ color: '#ff4d4f' }}>
                {orderData?.paySuccessDate ? new Date(orderData.paySuccessDate).toLocaleString() : '-'}
              </Text>
            </div>
          </div>

          <Divider style={{ margin: dividerMargin }} />

          {/* Rent moved here (F) */}
          <div style={{ display: 'flex', justifyContent: 'space-between' }}>
            <Text type="secondary">租金:</Text>
            <Text strong>{formatMoney(orderData?.hadPayRental || orderData?.receivedRental)} /{formatMoney(orderData?.originRent || orderData?.receivableRental || orderData?.rentalNeedPay)}</Text>
          </div>

          <Divider style={{ margin: dividerMargin }} />

          <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: 4, alignItems: 'center' }}>
            <Text type="secondary" style={{ display: 'flex', alignItems: 'center' }}>
              到期购买价:
              {order.orderRecord?.buyoutInfo?.buyout_list && order.orderRecord.buyoutInfo.buyout_list.length > 0 && (
                <InfoCircleOutlined
                  style={{ marginLeft: 4, color: '#1890ff', cursor: 'pointer' }}
                  onClick={() => setIsBuyoutModalVisible(true)}
                />
              )}
            </Text>
            {/* Use currentPurchasePrice if available, else buyoutPrice */}
            <Text style={{ color: '#1890ff' }}>{formatMoney(orderData?.buyoutPrice)}</Text>
          </div>
        </div>

        {/* Col 4: User (Flex: 2) */}
        <div style={{ flex: 2, padding: mainPadding, borderRight: '1px solid #f0f0f0' }}>
          <div style={{ marginBottom: 4, display: 'flex', alignItems: 'center', gap: 4 }}>
            <span style={{ fontSize: '14px', fontWeight: 700 }}>{orderEntity.userName} {orderEntity.userPhone}</span>
            <Paragraph copyable={{ text: `${orderEntity.userName} ${orderEntity.userPhone}` }} style={{ margin: 0, color: '#1890ff' }} />
          </div>
          <div style={{ color: '#666', lineHeight: '1.4', display: 'flex', alignItems: 'start', gap: 4 }}>
            <span>{formatAddress()}</span>
            <Paragraph copyable={{ text: formatAddress() }} style={{ margin: 0, color: '#1890ff' }} />
          </div>
          <div style={{ marginTop: 8, color: '#999', display: 'flex', alignItems: 'center', gap: 4 }}>
            注册手机: {safeGet(orderData?.userRegisterPhone)}
            {orderData?.userRegisterPhone && <Paragraph copyable={{ text: orderData.userRegisterPhone }} style={{ margin: 0, color: '#1890ff' }} />}
          </div>

          <Divider style={{ margin: dividerMargin }} />

          <div style={{ marginTop: 4, color: '#666', fontSize: 12 }}>
            <div style={{ display: 'flex', justifyContent: 'space-between' }}>
              <Text style={{ color: '#ff4d4f', textDecoration: 'underline', cursor: 'pointer' }}>公司: 成功下单{orderData?.orderStatCount?.companySuccessOrderCount || 0}次</Text>
              <Text style={{ color: '#ff4d4f', textDecoration: 'underline', cursor: 'pointer' }}>累计下单{orderData?.orderStatCount?.companyOrderCount || 0}次</Text>
            </div>
            {order.orderRecord?.orderStatistic && (
              <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: 4 }}>
                <Text style={{ textDecoration: 'underline', cursor: 'pointer' }}>人人租平台: 成功下单{order.orderRecord.orderStatistic.success_order}次</Text>
                <Text style={{ textDecoration: 'underline', cursor: 'pointer' }}>累计下单{order.orderRecord.orderStatistic.total_order}次</Text>
              </div>
            )}
          </div>
        </div>

        {/* Col 5: Status/Info (Flex: 1.5) - (E) */}
        <div style={{ flex: statusFlex, padding: mainPadding, borderRight: '1px solid #f0f0f0', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 16 }}>
          <div style={{ textAlign: 'center', width: '100%' }}>
            <div style={{ marginBottom: 8, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <span style={{ color: '#faad14', marginRight: 6 }}>●</span>
              <Text style={{ color: '#000' }}>人人租订单状态: {ORDER_STATUS_LABEL_MAP[orderData?.orderStatus || 0]}</Text>
            </div>

            {/* Condition: Apply Cancel (Red Box) */}
            {isApplyingCancel && cancelData && (
              <div style={{
                background: '#fff1f0',
                border: '1px solid #ffccc7',
                borderRadius: 4,
                padding: 8,
                marginTop: 8,
                textAlign: 'left'
              }}>
                <div style={{ display: 'flex', alignItems: 'center', marginBottom: 4 }}>
                  <ExclamationCircleFilled style={{ color: '#ff4d4f', marginRight: 8 }} />
                  <Text style={{ color: '#000' }}>申请取消类型: {cancelData.cancel_reason}</Text>
                </div>
                <div style={{ display: 'flex', alignItems: 'center', marginBottom: 4 }}>
                  <ExclamationCircleFilled style={{ color: '#ff4d4f', marginRight: 8 }} />
                  <Text style={{ color: '#000' }}>申请取消原因: {getCancelReasonText(cancelData.cancel_sub_reason || cancelData.cancel_reason)}</Text>
                </div>
                <div style={{ display: 'flex', alignItems: 'center', marginBottom: 4 }}>
                  <ClockCircleOutlined style={{ color: '#ff4d4f', marginRight: 8 }} />
                  <Text style={{ color: '#000' }}>申请时间: {cancelData.apply_cancel_date ? new Date(cancelData.apply_cancel_date).toLocaleString() : '-'}</Text>
                </div>
                {/* <div style={{ display: 'flex', alignItems: 'center' }}>
                      <CheckCircleOutlined style={{ color: '#52c41a', marginRight: 8 }} />
                      <Text style={{ color: '#52c41a' }}>拦截成功: {cancelData.auto_cancel_order_count_down_text || '即将自动取消'}</Text>
                   </div> */}
              </div>
            )}

            {/* Condition: Closed Order (Gray Box) */}
            {([21, 22, 23].includes(orderData?.orderStatus || 0)) && cancelData && !isApplyingCancel && (
              <div style={{
                background: '#f5f5f5',
                borderRadius: 4,
                padding: 8,
                marginTop: 8,
                textAlign: 'left'
              }}>
                <div style={{ marginBottom: 4 }}>
                  <Text type="secondary">订单取消类型: {cancelData.cancel_reason}</Text>
                </div>
                <div style={{ marginBottom: 4 }}>
                  <Text type="secondary">订单取消原因: {getCancelReasonText(cancelData.cancel_sub_reason || cancelData.cancel_reason)}</Text>
                </div>
                <div>
                  <Text type="secondary">订单取消时间: {cancelData.cancel_date ? new Date(cancelData.cancel_date).toLocaleString() : '-'}</Text>
                </div>
              </div>
            )}

            <Divider style={{ margin: dividerMargin }} />

            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <span style={{ color: '#faad14', marginRight: 6 }}>●</span>
              <Text style={{ color: '#000' }}>系统工单状态: {OrderStatusMap[orderEntity.orderStatus]}</Text>
            </div>

            {/* Condition: Cancelled Order (Status 40) - Same Style as Closed Order */}
            {/* {cancelData && !isApplyingCancel && ![21, 22, 23].includes(orderData?.orderStatus || 0) && (orderEntity.orderStatus === OrderStatus.Cancelled || orderData?.orderStatus === 40) && (
                <div style={{ 
                  background: '#f5f5f5', 
                  borderRadius: 4, 
                  padding: 8, 
                  marginTop: 8,
                  textAlign: 'left'
                }}>
                   <div style={{ marginBottom: 4 }}>
                      <Text type="secondary">订单取消类型: {cancelData.cancel_reason}</Text>
                   </div>
                   <div style={{ marginBottom: 4 }}>
                      <Text type="secondary">订单取消原因: {getCancelReasonText(cancelData.cancel_sub_reason || cancelData.cancel_reason)}</Text>
                   </div>
                   <div>
                      <Text type="secondary">订单取消时间: {cancelData.cancel_date ? new Date(cancelData.cancel_date).toLocaleString() : '-'}</Text>
                   </div>
                </div>
             )} */}
          </div>

          {/* Render Info */}
          {renderInfo && (
            <>
              <Divider style={{ margin: dividerMargin }} />
              <div style={{ width: '100%', textAlign: 'center' }}>
                {renderInfo(order)}
              </div>
            </>
          )}
        </div>

        {/* Col 6: Actions (Flex: 1) - (F) */}
        <div style={{ flex: 1, padding: mainPadding, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 12 }}>
          {orderData?.appType != "系统补订单" && renderActions ? renderActions(order) : null}
        </div>
      </div>

      {/* Bottom Bar: Auth & Risk (D) */}
      <div style={{ padding: '12px 16px', borderTop: '1px solid #f0f0f0', display: 'flex', flexDirection: 'column', gap: 8 }}>
        <Space split={<Divider type="vertical" />}>
          <Text type="secondary">认证资料: {safeGet(orderData?.userAuthName)}</Text>
          <Text type="secondary">{safeGet(orderData?.userAuthIdCard)}</Text>
          <Text type="secondary">{orderData?.faceRecognitionStatus}</Text>
          <Text type="secondary">性别: {safeGet(orderData?.userSex)}</Text>
          <Text type="secondary">年龄: {safeGet(orderData?.userAge)}</Text>
        </Space>

        {/* Risk Info below Auth Info */}
        {(() => {
          const qualitative = String(orderData?.riskQualitative ?? '').trim();
          const result = String(orderData?.riskResult ?? '').trim();
          const content = String(orderData?.riskContent ?? '').trim();
          const formatted = formatRiskQualitative(qualitative);

          if (qualitative) {
            return (
              <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                <Tag
                  color={formatted.tagColor as any}
                  style={{ borderRadius: '12px', padding: '0 10px' }}
                >
                  {formatted.tagText}
                </Tag>
                {result ? <Text style={{ color: formatted.textColor, fontSize: '12px' }}>{result}</Text> : null}
              </div>
            );
          }

          if (content) {
            return (
              <div style={{ display: 'flex', alignItems: 'center' }}>
                <Text style={{ color: '#faad14' }}>{content}</Text>
              </div>
            );
          }

          if (!qualitative && !content) {
            return (
              <div style={{ display: 'flex', alignItems: 'center' }}>
                <Text style={{ color: '#faad14' }}>【未风控】 尚未抓取到风控报告</Text>
              </div>
            );
          }

          return null;
        })()}
      </div>

      {/* Operation Logs & Buyout Info */}
      {(order.orderRecord?.operationLog && order.orderRecord.operationLog.length > 0) && (
        <div
          style={{ padding: '8px 16px', borderTop: '1px solid #f0f0f0', backgroundColor: '#fafafa', fontSize: 12, color: '#666', cursor: 'pointer' }}
          onClick={() => setIsLogModalVisible(true)}
        >
          {(() => {
            const logs = order.orderRecord!.operationLog!;
            const lastLog = logs[0];
            return (
              <div style={{ display: 'flex', alignItems: 'center' }}>
                <span>{lastLog.remark} - {lastLog.remark_by} - {lastLog.created_at} / {logs.length}次记录</span>
              </div>
            );
          })()}
        </div>
      )}

      {/* Accessory List Modal (B) */}
      <AccessoryModal
        visible={isAccessoryModalVisible}
        onClose={() => setIsAccessoryModalVisible(false)}
        accessoryData={orderData?.accessoryData}
      />

      {/* Buyout Info Modal */}
      <BuyoutInfoModal
        visible={isBuyoutModalVisible}
        onClose={() => setIsBuyoutModalVisible(false)}
        buyoutInfo={order.orderRecord?.buyoutInfo}
      />

      <OperationLogModal
        visible={isLogModalVisible}
        onClose={() => setIsLogModalVisible(false)}
        logs={order.orderRecord?.operationLog}
      />
      <LogisticsTrackModal
        visible={isTrackModalVisible}
        onClose={() => setIsTrackModalVisible(false)}
        logisticsNo={orderData?.logisticsInfo?.logisticNumber}
      />
    </div>
  );
};

export default OrderBlockShow;
